// src/components/AppLayout.tsx
import React from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { CssVarsProvider, extendTheme } from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';
import { GlobalStyles } from '@mui/joy';
import { Box, List, ListItem, ListItemButton, ListItemDecorator, Typography } from '@mui/joy';
import { Home, Package, Truck, Users, Box as BoxIcon, FileText, LogOut } from 'lucide-react';
import { PAGE_TRANSLATIONS } from '../conf';
import useAuthStore from '../stores/authStore';

// Custom theme (optional)
const theme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: {
          // Customize your primary color if needed
        }
      }
    },
    dark: {
      palette: {
        primary: {
          // Customize your primary color for dark mode if needed
        }
      }
    }
  }
});

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();

  const handleLogout = () => {
    logout();
    // The navigation to login might be handled by the auth store already
    // If not, we'll keep this line
    navigate('/login');
  };

  return (
    <CssVarsProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <GlobalStyles
        styles={{
          html: {
            height: '100%',
          },
          body: {
            height: '100%',
          },
          '#root': {
            height: '100%',
            display: 'flex',
          },
          '@media print': {
            '.sidebar': {
              display: 'none !important',
            },
            '.main-content': {
              margin: '0 !important',
              padding: '0 !important',
              width: '100% !important',
              maxWidth: 'none !important',
            }
          }
        }}
      />
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Box 
          className="sidebar"
          sx={{ 
            width: 210, 
            minWidth: 210, 
            bgcolor: 'background.body', 
            p: 2,
            position: 'relative', 
            height: '100vh',
            display: 'flex',
            flexDirection: 'column' 
          }}
        >
          <Typography level="h4" sx={{ mb: 2 }}>SRS</Typography>
          <List sx={{ flexGrow: 1 }}>
            <ListItem>
              <ListItemButton component={Link} to="/dashboard">
                <ListItemDecorator>
                  <Home size={20} />
                </ListItemDecorator>
                {PAGE_TRANSLATIONS.DASHBOARD}
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton component={Link} to="/product-weight">
                <ListItemDecorator>
                  <Package size={20} />
                </ListItemDecorator>
                {PAGE_TRANSLATIONS.PRODUCT_WEIGHT}
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton component={Link} to="/truck-weight">
                <ListItemDecorator>
                  <Truck size={20} />
                </ListItemDecorator>
                {PAGE_TRANSLATIONS.TRUCK_WEIGHT}
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton component={Link} to="/customer">
                <ListItemDecorator>
                  <Users size={20} />
                </ListItemDecorator>
                ลูกค้า
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton component={Link} to="/product">
                <ListItemDecorator>
                  <BoxIcon size={20} />
                </ListItemDecorator>
                สินค้า
              </ListItemButton>
            </ListItem>
            {user?.isAdmin && (
              <ListItem>
                <ListItemButton component={Link} to="/bill">
                  <ListItemDecorator>
                    <FileText size={20} />
                  </ListItemDecorator>
                  บิล
                </ListItemButton>
              </ListItem>
            )}
          </List>
          
          {/* Logout button at bottom */}
          <Box sx={{ 
            borderTop: '1px solid',
            borderColor: 'divider',
            pt: 1.5,
            mt: 2
          }}>
            <ListItem>
              <ListItemButton 
                onClick={handleLogout}
                sx={{ 
                  color: 'warning.600', 
                  '&:hover': { 
                    bgcolor: 'warning.100',
                    color: 'warning.700'
                  }
                }}
              >
                <ListItemDecorator sx={{ mr: 2 }}>
                  <LogOut size={20} />
                </ListItemDecorator>
                {PAGE_TRANSLATIONS.LOGOUT}
              </ListItemButton>
            </ListItem>
          </Box>
        </Box>
        <Box className="main-content" sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </CssVarsProvider>
  );
};

export default AppLayout;